/**
 * useCanvasChallenges — Evaluates challenge rules against canvas state.
 *
 * Client-side evaluation using the shared challengeEngine.
 * No API calls needed — pure computation.
 */

import { useMemo } from 'react'
import { evaluateChallenges, type SectionStats, type SectionItem, type Challenge } from '@ispot/shared'
import type { ChallengeRule } from '@ispot/shared'

interface UseCanvasChallengesOptions {
  rules: ChallengeRule[] | undefined
  sectionStats: Record<string, SectionStats>
  sectionItems: Record<string, SectionItem[]>
}

export function useCanvasChallenges({
  rules,
  sectionStats,
  sectionItems,
}: UseCanvasChallengesOptions): {
  challenges: Challenge[]
  criticalCount: number
  strategicCount: number
  opportunityCount: number
} {
  const challenges = useMemo(() => {
    if (!rules || rules.length === 0) return []
    return evaluateChallenges(rules, sectionStats, sectionItems)
  }, [rules, sectionStats, sectionItems])

  const criticalCount = challenges.filter((c) => c.severity === 'critical').length
  const strategicCount = challenges.filter((c) => c.severity === 'strategic').length
  const opportunityCount = challenges.filter((c) => c.severity === 'opportunity').length

  return { challenges, criticalCount, strategicCount, opportunityCount }
}
